/**
 * Anchor Link Scroll Handler
 * Handles smooth scrolling to anchor IDs with accordion/collapsible content support
 */

class AnchorLinkScroll {
  constructor() {
    this.headerOffset = this.getHeaderOffset();
    this.init();
  }

  /**
   * Calculate header offset for scroll positioning
   */
  getHeaderOffset() {
    const header = document.querySelector('header, .header, [role="banner"]');
    if (header) {
      const headerHeight = header.offsetHeight;
      // Add extra spacing for readability - 5x on desktop, 3x on mobile
      const isMobile = window.innerWidth < 720;
      const multiplier = isMobile ? 4 : 5;
      return headerHeight * multiplier;
    }
    return 100; // Default fallback
  }

  /**
   * Initialize event listeners
   */
  init() {
    // Listen for clicks on elements with js-link class
    document.addEventListener('click', (e) => {
      const linkElement = e.target.closest('.js-link');
      if (linkElement) {
        e.preventDefault();
        const targetId = linkElement.getAttribute('js-link-target');
        if (targetId && targetId.startsWith('#')) {
          this.scrollToAnchor(targetId);
        }
      }
    });

    // Also handle regular anchor links that start with #
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (link && link.getAttribute('href') !== '#') {
        const href = link.getAttribute('href');
        const targetId = href;
        const targetElement = document.querySelector(targetId);
        
        // Only handle if target exists and might be in accordion
        if (targetElement) {
          const isInAccordion = targetElement.closest('details, .accordion-item');
          if (isInAccordion) {
            e.preventDefault();
            this.scrollToAnchor(targetId);
          }
        }
        else {
          e.preventDefault();
        }
      }
    });
  }

  /**
   * Open all parent accordions/details elements
   */
  openParentAccordions(element) {
    let current = element;
    const accordionsToOpen = [];

    // Find all parent accordion/details elements
    while (current && current !== document.body) {
      if (current.tagName === 'DETAILS' || current.classList.contains('accordion-item')) {
        accordionsToOpen.unshift(current); // Add to beginning to open from top down
      }
      current = current.parentElement;
    }

    // Open each accordion in sequence
    accordionsToOpen.forEach((accordion) => {
      if (accordion.tagName === 'DETAILS') {
        accordion.open = true;
      } else if (accordion.classList.contains('accordion-item')) {
        // Handle custom accordion implementation
        accordion.setAttribute('open', '');
        const summary = accordion.querySelector('summary, .accordion-item-title');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
        }
      }
    });

    return accordionsToOpen.length > 0;
  }

  /**
   * Scroll to anchor with smooth animation
   */
  scrollToAnchor(targetId) {
    // Remove # if present
    const cleanId = targetId.replace('#', '');
    const targetElement = document.getElementById(cleanId);

    if (!targetElement) {
      console.warn(`Anchor target not found: ${targetId}`);
      return;
    }

    // Open parent accordions if nested
    const wasInAccordion = this.openParentAccordions(targetElement);

    // Wait a bit if we opened accordions to let them animate
    const delay = wasInAccordion ? 300 : 0;

    setTimeout(() => {
      // Recalculate header offset in case it changed
      this.headerOffset = this.getHeaderOffset();

      // Calculate scroll position
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - this.headerOffset;

      // Smooth scroll to position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Optional: Add highlight effect to target
      this.highlightTarget(targetElement);
    }, delay);
  }

  /**
   * Add temporary highlight effect to target element
   */
  highlightTarget(element) {
    // Add a temporary class for visual feedback
    element.style.transition = 'background-color 0.3s ease';
    const originalBg = element.style.backgroundColor;
    
    element.style.backgroundColor = 'rgba(var(--color-text-rgb, 0, 0, 0), 0.05)';
    
    setTimeout(() => {
      element.style.backgroundColor = originalBg;
      setTimeout(() => {
        element.style.transition = '';
      }, 300);
    }, 1000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AnchorLinkScroll();
  });
} else {
  new AnchorLinkScroll();
}
